import React, { useEffect, useState } from "react";
import {
  Pagination as BSPagination,
  DropdownButton,
  MenuItem,
} from "react-bootstrap";
import useDebounce from "src/hooks/useDebounce"; 

const DEFAULT_PER_PAGE = 10;

const ComparableObjectList = ({
  onSelectItem,
  onPaginationChange,
  comparableObject,
  totalCount,
  frameworkId
}) => {
  const [prevFrameworkId, setPrevFrameworkId] = useState("")
  const [searchTerm, setSearchTerm] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_PER_PAGE)
  const [pageCount, setPageCount] = useState(0)
  const [selectedItemId, setSelectedItemId] = useState("")

  useEffect(() => {
    const pageCount = Math.ceil(totalCount / itemsPerPage);
    setPageCount(pageCount)
  }, [itemsPerPage, totalCount])

  useEffect(() => {
    if (prevFrameworkId !== frameworkId) {
      setPrevFrameworkId(frameworkId)
      resetParams()
    }
  }, [prevFrameworkId, frameworkId])

  const resetParams = () => {
    setSearchTerm("")
    setCurrentPage(1)
    setItemsPerPage(DEFAULT_PER_PAGE)
  }

  const onPaginationChangeDebounced = useDebounce(onPaginationChange, 200)

  const handleSearchChange = (e) => {
    const searchExpr = e.target.value
    onPaginationChangeDebounced(itemsPerPage, offset(1, itemsPerPage), searchExpr)
  
    
    setSearchTerm(searchExpr)
    setCurrentPage(1)
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > pageCount || pageNumber === currentPage) {
      return
    }

    onPaginationChange(itemsPerPage, offset(pageNumber, itemsPerPage), searchTerm)
    
    setCurrentPage(pageNumber)
  };

  const handlePerPageChange = (newPerPage) => {
    onPaginationChange(newPerPage, offset(1, newPerPage), searchTerm)

    setItemsPerPage(newPerPage)
    setCurrentPage(1)
  };

  const offset = (currentPage, itemsPerPage) => {
    return (currentPage - 1) * itemsPerPage
  }

  const getPaginationItems = () => {
    const items = [];
    const maxPagesToShow = 5;
    const halfMaxPagesToShow = Math.floor(maxPagesToShow / 2);

    let startPage = Math.max(1, currentPage - halfMaxPagesToShow);
    let endPage = Math.min(pageCount, currentPage + halfMaxPagesToShow);

    if (currentPage <= halfMaxPagesToShow) {
      endPage = Math.min(pageCount, maxPagesToShow);
    } else if (currentPage + halfMaxPagesToShow >= pageCount) {
      startPage = Math.max(1, pageCount - maxPagesToShow + 1);
    }

    if (startPage > 1) {
      items.push(
        <BSPagination.Item key={1} onClick={() => handlePageChange(1)}>
          1
        </BSPagination.Item>
      );
      if (startPage > 2) {
        items.push(<BSPagination.Ellipsis key="start-ellipsis" disabled />);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <BSPagination.Item
          key={i}
          active={i === currentPage}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </BSPagination.Item>
      );
    }

    if (endPage < pageCount) {
      if (endPage < pageCount - 1) {
        items.push(<BSPagination.Ellipsis key="end-ellipsis" disabled />);
      }
      items.push(
        <BSPagination.Item
          key={pageCount}
          onClick={() => handlePageChange(pageCount)}
        >
          {pageCount}
        </BSPagination.Item>
      );
    }

    return items;
  };

  const handleItemClick = (item) => {
    setSelectedItemId(item.name + item.code)
    if (onSelectItem) {
      onSelectItem(item);
    }
  };

  return (
    <div
      className="flex-grow-1 p-3"
      style={{ borderRight: "1px solid #ddd" }}
    >
      <input
        type="text"
        placeholder="Search by Code or Name"
        value={searchTerm}
        onChange={handleSearchChange}
        className="form-control mb-3"
      />
      <ul className="list-group">
        {comparableObject.map((obj) => (
          <li
            key={obj.code + obj.name}
            className="list-group-item"
            onClick={() => handleItemClick(obj)}
            style={{
              cursor: "pointer",
              backgroundColor: selectedItemId === obj.name + obj.code ? "#e0f3ff" : "white",
              transition: "background-color 0.3s",
            }}
            onMouseEnter={(e) => {
              if (selectedItemId !== obj.name + obj.code) {
                e.currentTarget.style.backgroundColor = "#f0f8ff";
              }
            }}
            onMouseLeave={(e) => {
              if (selectedItemId !== obj.name + obj.code) {
                e.currentTarget.style.backgroundColor = "white";
              }
            }}
            title={obj.name || "[No name]"}
          >
            {`${
              obj.name?.length > 60
                ? obj.name.slice(0, 60) + "..."
                : obj.name || "[No name]"
            } (${obj.code})`}
          </li>
        ))}
      </ul>

      <div className="d-flex justify-content-between align-items-center mt-3">
        <BSPagination>
          <BSPagination.First
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
          />
          <BSPagination.Prev
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          />
          {getPaginationItems()}
          <BSPagination.Next
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === pageCount}
          />
          <BSPagination.Last
            onClick={() => handlePageChange(pageCount)}
            disabled={currentPage === pageCount}
          />
        </BSPagination>

        <DropdownButton
          title={`Items per page: ${itemsPerPage}`}
          onSelect={(e) => handlePerPageChange(Number(e))}
          id="dropdown-per-page"
          variant="secondary"
        >
          {[10, 20, 50, 100].map((num) => (
            <MenuItem key={num} eventKey={num} active={itemsPerPage === num}>
              {num}
            </MenuItem>
          ))}
        </DropdownButton>
      </div>
    </div>
  )
}

export default ComparableObjectList;
